import { resolveBranchRef } from './branchResolver';

const toEntries = (value) => Object.entries(value || {});

export const branchRefsLooselyMatch = (a, b) => {
  if (!a || !b) return false;
  if (a.id && b.id && String(a.id) === String(b.id)) return true;
  if (a.slug && b.slug && String(a.slug) === String(b.slug)) return true;
  const an = String(a.name || '').trim().toLowerCase();
  const bn = String(b.name || '').trim().toLowerCase();
  return Boolean(an && bn && an === bn);
};

/** Enrollment branch, falling back to student / user profile (common when enrollments omit branchId). */
export const resolveEnrollmentBranchRef = (
  enrollment,
  { student, user } = {},
  branchCatalogs = {},
) => {
  const fromFields = (branchId, branch, branchName) =>
    resolveBranchRef(
      {
        branchId,
        branch,
        branchName,
      },
      branchCatalogs,
    );

  let r = fromFields(enrollment?.branchId, enrollment?.branch, enrollment?.branchName);
  if (r?.id || r?.name) return r;

  if (student) {
    r = fromFields(
      student.branchId || student.personalDetails?.branchId,
      student.branch ||
        student.personalDetails?.branch ||
        student.publicProfile?.branch,
      student.branchName || student.personalDetails?.branchName,
    );
    if (r?.id || r?.name) return r;
  }

  if (user) {
    r = fromFields(
      user.branchId || user.personalDetails?.branchId,
      user.branch || user.personalDetails?.branch,
      user.branchName || user.personalDetails?.branchName,
    );
  }
  return r;
};

export const toArray = (value) =>
  toEntries(value).map(([id, data]) => ({
    id,
    ...data,
  }));

export const buildStudentCourseMap = (courses = {}, enrollments = []) => {
  const courseMap = {};

  enrollments.forEach((enrollment) => {
    const course = courses[enrollment.courseId];
    if (!course) return;

    courseMap[enrollment.courseId] = {
      id: enrollment.courseId,
      ...course,
      enrollment,
      progress:
        typeof enrollment.progress === 'number' ? enrollment.progress : 0,
      totalLessons: Array.isArray(course.lessons) ? course.lessons.length : 0,
      completedLessons: extractCompletedLessons(enrollment.completedLessons),
    };
  });

  return courseMap;
};

export const buildStudentEnrollmentStatusMap = ({
  enrollments = [],
  enrollmentRequests = [],
  paymentRequests = [],
  payments = [],
}) => {
  const statusMap = {};

  enrollments.forEach((enrollment) => {
    statusMap[enrollment.courseId] = enrollment.status || 'active';
  });

  enrollmentRequests.forEach((request) => {
    if (request.status === 'pending' && !statusMap[request.courseId]) {
      statusMap[request.courseId] = 'request_submitted';
    }
  });

  paymentRequests.forEach((request) => {
    if (
      (!statusMap[request.courseId] ||
        statusMap[request.courseId] === 'request_submitted') &&
      (request.status === 'processing' || request.status === 'pending')
    ) {
      statusMap[request.courseId] = 'payment_pending';
    }
  });

  payments.forEach((payment) => {
    if (payment.status === 'pending' && !statusMap[payment.courseId]) {
      statusMap[payment.courseId] = 'payment_review';
    }
  });

  return statusMap;
};

export const getReadableEnrollmentStatus = (status) => {
  switch (status) {
    case 'active':
      return {
        label: 'Active',
        className: 'bg-green-100 text-green-800',
      };
    case 'completed':
      return {
        label: 'Completed',
        className: 'bg-blue-100 text-blue-800',
      };
    case 'request_submitted':
      return {
        label: 'Request Submitted',
        className: 'bg-yellow-100 text-yellow-800',
      };
    case 'payment_pending':
      return {
        label: 'Payment Pending',
        className: 'bg-pink-100 text-pink-900',
      };
    case 'payment_review':
      return {
        label: 'Payment Under Review',
        className: 'bg-pink-100 text-pink-900',
      };
    default:
      return {
        label: 'Available',
        className: 'bg-gray-100 text-gray-700',
      };
  }
};

/** Rows that share the same `batchId` are one logical offering (multi-branch). */
export const countDistinctBatchOfferings = (batches = []) => {
  const list = Array.isArray(batches) ? batches : [];
  const keys = new Set();
  list.forEach((b, i) => {
    keys.add(
      b?.batchId != null && String(b.batchId).length
        ? String(b.batchId)
        : `__row_${i}`,
    );
  });
  return keys.size;
};

export const buildCourseBatchSeatSummary = (
  course,
  enrollments = [],
  {
    students = {},
    users = {},
    branchCatalogs = {},
  } = {},
) => {
  const activeEnrollments = enrollments.filter(
    (enrollment) =>
      enrollment.courseId === course.id && enrollment.status === 'active',
  );

  const batches = course.batches || [];

  return batches.map((batch, batchIndex) => {
    const enrolledStudents = activeEnrollments.filter((enrollment) => {
      const student = students[enrollment.studentId];
      const user = users[enrollment.studentId];
      const enrollHasBranchId = Boolean(
        enrollment.branchId != null && String(enrollment.branchId).trim() !== '',
      );

      const batchIdMatches =
        String(enrollment.batchId) === String(batch.batchId) ||
        Number(enrollment.batchId) === batchIndex;

      const batchIndexMatches =
        enrollment.batchIndex != null &&
        Number(enrollment.batchIndex) === batchIndex;

      if (!batchIdMatches && !batchIndexMatches) {
        return false;
      }

      const sharedBatchIdCount = batches.filter(
        (b) => String(b.batchId) === String(batch.batchId),
      ).length;
      const batchBranchRef = batch.branchId
        ? resolveBranchRef(batch, branchCatalogs)
        : null;

      // Multi-branch rows share one batchId; enrollment.branchId is often copied
      // from the first row only — prefer student/user profile when it matches a
      // sibling branch, and only then fall back to enrollment.branchId.
      if (batch.branchId && sharedBatchIdCount > 1) {
        const profileOnlyRef = resolveEnrollmentBranchRef(
          {},
          { student, user },
          branchCatalogs,
        );
        const profileMatchesSomeRow =
          (profileOnlyRef?.id || profileOnlyRef?.name) &&
          batches.some((b) => {
            if (String(b.batchId) !== String(batch.batchId) || !b.branchId) {
              return false;
            }
            return branchRefsLooselyMatch(
              profileOnlyRef,
              resolveBranchRef(b, branchCatalogs),
            );
          });
        if (profileMatchesSomeRow) {
          return (
            batchBranchRef &&
            branchRefsLooselyMatch(profileOnlyRef, batchBranchRef)
          );
        }
        if (enrollHasBranchId) {
          return String(enrollment.branchId) === String(batch.branchId);
        }
        const enrollBranchRef = resolveEnrollmentBranchRef(
          enrollment,
          { student, user },
          branchCatalogs,
        );
        if (
          enrollBranchRef &&
          batchBranchRef &&
          branchRefsLooselyMatch(enrollBranchRef, batchBranchRef)
        ) {
          return true;
        }
        if (enrollment.batchIndex != null) {
          return Number(enrollment.batchIndex) === batchIndex;
        }
        const firstOfBatchId = batches.findIndex(
          (b) => String(b.batchId) === String(batch.batchId),
        );
        return batchIndex === firstOfBatchId;
      }

      if (batch.branchId && enrollHasBranchId) {
        return String(enrollment.branchId) === String(batch.branchId);
      }

      if (batch.branchId && !enrollHasBranchId) {
        const enrollBranchRef = resolveEnrollmentBranchRef(
          enrollment,
          { student, user },
          branchCatalogs,
        );
        if (
          enrollBranchRef &&
          batchBranchRef &&
          branchRefsLooselyMatch(enrollBranchRef, batchBranchRef)
        ) {
          return true;
        }
        return (
          enrollment.batchIndex == null ||
          Number(enrollment.batchIndex) === batchIndex
        );
      }

      if (enrollment.batchIndex != null) {
        return Number(enrollment.batchIndex) === batchIndex;
      }

      return batchIdMatches;
    });

    return {
      ...batch,
      batchIndex,
      enrolledCount: enrolledStudents.length,
      availableSeats: Math.max(
        0,
        Number(batch.maxStudents || 0) - enrolledStudents.length,
      ),
      enrolledStudents,
    };
  });
};

export const buildAdminPendingEnrollmentList = ({
  courses = {},
  enrollments = [],
  enrollmentRequests = [],
  students = {},
}) =>
  enrollmentRequests
    .filter((request) => request.status === 'pending')
    .map((request) => {
      const student = students[request.studentId];
      const course = courses[request.courseId];
      const batch = (course?.batches || []).find(
        (item, index) =>
          String(item.batchId) === String(request.batchId) ||
          String(index) === String(request.batchId),
      );
      const activeCount = enrollments.filter(
        (enrollment) =>
          enrollment.courseId === request.courseId &&
          enrollment.status === 'active' &&
          (String(enrollment.batchId) === String(request.batchId) ||
            String(enrollment.batchId) === String(batch?.batchId)),
      ).length;

      return {
        ...request,
        studentName:
          request.studentName ||
          formatStudentName(student) ||
          'Unknown student',
        courseName: request.courseName || course?.name || 'Unknown course',
        batchName: batch?.batchId || request.batchId,
        branch: batch?.branch || 'Not specified',
        maxStudents: Number(batch?.maxStudents || 0),
        activeCount,
        availableSeats: Math.max(
          0,
          Number(batch?.maxStudents || 0) - activeCount,
        ),
      };
    })
    .sort((a, b) => new Date(a.requestDate) - new Date(b.requestDate));

export const buildAdminPendingPaymentList = ({
  payments = [],
  paymentRequests = [],
  students = {},
  courses = {},
}) => {
  const requestMap = paymentRequests.reduce((acc, request) => {
    acc[request.id] = request;
    return acc;
  }, {});

  return payments
    .filter((payment) => payment.status === 'pending')
    .map((payment) => {
      const request = requestMap[payment.requestId];
      const student = students[payment.studentId];
      const course = courses[payment.courseId];

      return {
        ...payment,
        studentName:
          payment.studentName ||
          request?.studentName ||
          formatStudentName(student) ||
          'Unknown student',
        courseName:
          payment.courseName || request?.courseName || course?.name || 'Course',
        batchName: payment.batchName || request?.batchName || payment.batchId,
      };
    })
    .sort((a, b) => new Date(b.paymentDate) - new Date(a.paymentDate));
};

export const buildStudentJourneyState = ({
  user,
  student,
  enrollments = [],
  enrollmentRequests = [],
  paymentRequests = [],
  payments = [],
  courses = {},
}) => {
  const activeEnrollments = enrollments.filter(
    (enrollment) => enrollment.status === 'active',
  );
  const completedEnrollments = enrollments.filter(
    (enrollment) => enrollment.status === 'completed',
  );
  const completedLessonCount = activeEnrollments.reduce(
    (count, enrollment) =>
      count + extractCompletedLessons(enrollment.completedLessons).length,
    0,
  );
  const verifiedPaymentCount = payments.filter(
    (payment) => payment.status === 'completed',
  ).length;
  const learningMomentum = activeEnrollments.filter(
    (enrollment) => Number(enrollment.progress || 0) > 0,
  ).length;

  const checklist = [
    {
      key: 'create-account',
      label: 'Create account',
      completed: Boolean(user?.uid),
    },
    {
      key: 'verify-email',
      label: 'Verify email',
      completed: Boolean(user?.emailVerified),
    },
    {
      key: 'complete-profile',
      label: 'Complete profile',
      completed: isProfileComplete(user, student),
    },
    {
      key: 'choose-course',
      label: 'Choose a course',
      completed:
        activeEnrollments.length > 0 || enrollmentRequests.length > 0,
    },
    {
      key: 'submit-enrollment',
      label: 'Submit enrollment',
      completed:
        activeEnrollments.length > 0 || enrollmentRequests.length > 0,
    },
    {
      key: 'submit-first-payment',
      label: 'Submit first payment or receipt',
      completed:
        verifiedPaymentCount > 0 ||
        paymentRequests.some((request) => request.paymentSubmitted),
    },
    {
      key: 'start-first-lesson',
      label: 'Start first lesson',
      completed: completedLessonCount > 0,
    },
  ];

  const completionPercentage = Math.round(
    (checklist.filter((item) => item.completed).length / checklist.length) *
      100,
  );

  const achievements = [
    {
      key: 'profileReady',
      label: 'Profile Ready',
      unlocked: checklist[2].completed,
    },
    {
      key: 'firstEnrollment',
      label: 'First Enrollment',
      unlocked:
        activeEnrollments.length > 0 || enrollmentRequests.length > 0,
    },
    {
      key: 'paymentSubmitted',
      label: 'Payment Submitted',
      unlocked:
        verifiedPaymentCount > 0 ||
        paymentRequests.some((request) => request.paymentSubmitted),
    },
    {
      key: 'firstLessonCompleted',
      label: 'First Lesson Completed',
      unlocked: completedLessonCount > 0,
    },
    {
      key: 'halfwayThere',
      label: 'Halfway There',
      unlocked: activeEnrollments.some(
        (enrollment) => Number(enrollment.progress || 0) >= 50,
      ),
    },
    {
      key: 'courseFinisher',
      label: 'Course Finisher',
      unlocked:
        completedEnrollments.length > 0 ||
        activeEnrollments.some(
          (enrollment) => Number(enrollment.progress || 0) === 100,
        ),
    },
  ];

  const nextAction =
    !checklist[1].completed
      ? {
          title: 'Verify your email to unlock the student dashboard',
          actionLabel: 'Open verification',
          href: '/verify-email',
        }
      : !checklist[2].completed
      ? {
          title: 'Complete your profile so admins can review you faster',
          actionLabel: 'Complete profile',
          href: '/profile',
        }
      : !checklist[3].completed
      ? {
          title: 'Choose a course and request a batch seat',
          actionLabel: 'Browse courses',
          href: '/courses/available',
        }
      : !checklist[5].completed
      ? {
          title: 'Submit your first payment or receipt to move forward',
          actionLabel: 'Open payments',
          href: '/payments',
        }
      : !checklist[6].completed
      ? {
          title: 'Start your first lesson and build momentum',
          actionLabel: 'Open my courses',
          href: '/courses/my-courses',
        }
      : {
          title: 'Keep your learning momentum going this week',
          actionLabel: 'Continue learning',
          href: '/courses/my-courses',
        };

  const coursePlans = activeEnrollments.map((enrollment) => {
    const course = courses[enrollment.courseId];
    const progress = Number(enrollment.progress || 0);
    const nextMilestone =
      progress >= 100
        ? 'Course complete'
        : progress >= 50
        ? 'Finish the remaining lessons'
        : progress > 0
        ? 'Reach 50% progress'
        : 'Complete your first lesson';

    return {
      courseId: enrollment.courseId,
      courseName: course?.name || 'Course',
      progress,
      nextMilestone,
    };
  });

  return {
    checklist,
    completionPercentage,
    achievements,
    nextAction,
    learningMomentum,
    completedLessonCount,
    coursePlans,
  };
};

export const buildAchievementsPayload = (journeyState) =>
  journeyState.achievements.reduce((acc, achievement) => {
    acc[achievement.key] = {
      unlocked: achievement.unlocked,
      updatedAt: new Date().toISOString(),
    };
    return acc;
  }, {});

export const formatStudentName = (student) => {
  if (!student) return '';

  const personalDetails = student.personalDetails || student;
  return `${personalDetails.firstName || ''} ${
    personalDetails.lastName || ''
  }`.trim();
};

export const extractCompletedLessons = (completedLessons) =>
  toEntries(completedLessons)
    .filter(([, value]) => value === true || value?.completed)
    .map(([id, value]) => ({
      id,
      completedAt: value?.completedAt || null,
    }));

const isProfileComplete = (user, student) => {
  const personalDetails = student?.personalDetails || {};

  return Boolean(
    (user?.firstName || personalDetails.firstName) &&
      (user?.lastName || personalDetails.lastName) &&
      user?.email &&
      (personalDetails.phone || user?.phone || user?.phoneNumber) &&
      personalDetails.address,
  );
};
